import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { PageLayout } from '../components/layout/PageLayout';
import { useAuth } from '../context/AuthContext';
import { api, formatMYR, formatDate } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Clock,
  CheckCircle,
  XCircle,
  StopCircle,
  Eye,
  ThumbsUp,
  ThumbsDown,
  Flag,
  Loader2,
  Users,
  Target,
  Calendar,
  Mail,
  Trash2,
  AlertTriangle,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { AdminBroadcastForm } from '../components/admin/AdminBroadcastForm';

const statusConfig = {
  PENDING_REVIEW: {
    icon: Clock,
    color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    label: 'Pending Review',
  },
  APPROVED: {
    icon: CheckCircle,
    color: 'bg-green-100 text-green-800 border-green-200',
    label: 'Approved',
  },
  REJECTED: {
    icon: XCircle,
    color: 'bg-red-100 text-red-800 border-red-200',
    label: 'Rejected',
  },
  ENDED: {
    icon: StopCircle,
    color: 'bg-gray-100 text-gray-800 border-gray-200',
    label: 'Ended',
  },
};

export default function AdminDashboardPage() {
  const { isAdmin } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('PENDING_REVIEW');
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    fetchCampaigns();
  }, [activeTab]);

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/admin/campaigns?status_filter=${activeTab}`);
      setCampaigns(response.data);
    } catch (error) {
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (campaignId, newStatus) => {
    setActionLoading(campaignId);
    try {
      await api.patch(`/campaigns/${campaignId}/status`, { status: newStatus });
      toast.success(`Campaign ${newStatus.toLowerCase().replace('_', ' ')}`);
      fetchCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteCampaign = async (campaignId) => {
    setActionLoading(campaignId);
    try {
      await api.delete(`/admin/campaigns/${campaignId}`);
      toast.success('Campaign deleted permanently');
      fetchCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete campaign');
    } finally {
      setActionLoading(null);
    }
  };

  if (!isAdmin) {
    return null;
  }

  const tabs = [
    { id: 'PENDING_REVIEW', label: 'Pending', icon: Clock },
    { id: 'APPROVED', label: 'Approved', icon: CheckCircle },
    { id: 'REJECTED', label: 'Rejected', icon: XCircle },
    { id: 'ENDED', label: 'Ended', icon: StopCircle },
  ];

  return (
    <PageLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground">Admin Dashboard</h1>
          <p className="text-muted-foreground mt-1">
            Review and manage campaigns
          </p>
        </div>

        <div className="mb-8">
          <AdminBroadcastForm />
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-secondary/50 p-1 rounded-full inline-flex">
            {tabs.map((tab) => (
              <TabsTrigger
                key={tab.id}
                value={tab.id}
                className="rounded-full gap-2"
                data-testid={`admin-tab-${tab.id.toLowerCase()}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {tabs.map((tab) => (
            <TabsContent key={tab.id} value={tab.id}>
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <div className="flex flex-col items-center gap-4">
                    <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                    <p className="text-muted-foreground">Loading campaigns...</p>
                  </div>
                </div>
              ) : campaigns.length === 0 ? (
                <div className="text-center py-20">
                  <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-secondary flex items-center justify-center">
                    <tab.icon className="w-10 h-10 text-muted-foreground" />
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    No {tab.label.toLowerCase()} campaigns
                  </h3>
                </div>
              ) : (
                <div className="space-y-4" data-testid="admin-campaigns-list">
                  {campaigns.map((campaign) => (
                    <Card key={campaign.id} className="overflow-hidden">
                      <CardContent className="p-6">
                        <div className="flex flex-col lg:flex-row lg:items-center gap-6">
                          {/* Campaign Image */}
                          <div className="w-full lg:w-48 h-32 rounded-xl overflow-hidden bg-secondary flex-shrink-0">
                            {campaign.image_url ? (
                              <img
                                src={campaign.image_url}
                                alt={campaign.title}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                                No image
                              </div>
                            )}
                          </div>

                          {/* Campaign Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-4 mb-3">
                              <div>
                                <h3 className="text-lg font-semibold text-foreground line-clamp-1">
                                  {campaign.title}
                                </h3>
                                <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                                  <Mail className="w-4 h-4" />
                                  {campaign.owner_email}
                                </div>
                              </div>
                              <Badge className={`${statusConfig[campaign.status].color} border flex-shrink-0`}>
                                {statusConfig[campaign.status].label}
                              </Badge>
                            </div>

                            <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
                              {campaign.description}
                            </p>

                            {/* Stats */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                              <div className="flex items-center gap-2">
                                <Target className="w-4 h-4 text-primary" />
                                <span>{formatMYR(campaign.target_cents)}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Users className="w-4 h-4 text-primary" />
                                <span>{campaign.donation_count} donors</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-primary">
                                  {formatMYR(campaign.raised_cents)}
                                </span>
                                <span className="text-muted-foreground">raised</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-muted-foreground" />
                                <span>{formatDate(campaign.deadline_date)}</span>
                              </div>
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex flex-row lg:flex-col gap-2 flex-shrink-0">
                            <Link to={`/campaigns/${campaign.id}`}>
                              <Button variant="outline" className="rounded-full gap-2 w-full" data-testid={`view-campaign-${campaign.id}`}>
                                <Eye className="w-4 h-4" />
                                View
                              </Button>
                            </Link>

                            {campaign.status === 'PENDING_REVIEW' && (
                              <>
                                <Button
                                  onClick={() => handleStatusChange(campaign.id, 'APPROVED')}
                                  disabled={actionLoading === campaign.id}
                                  className="rounded-full gap-2 bg-green-600 hover:bg-green-700"
                                  data-testid={`approve-campaign-${campaign.id}`}
                                >
                                  {actionLoading === campaign.id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <ThumbsUp className="w-4 h-4" />
                                  )}
                                  Approve
                                </Button>
                                <Button
                                  onClick={() => handleStatusChange(campaign.id, 'REJECTED')}
                                  disabled={actionLoading === campaign.id}
                                  variant="destructive"
                                  className="rounded-full gap-2"
                                  data-testid={`reject-campaign-${campaign.id}`}
                                >
                                  {actionLoading === campaign.id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <ThumbsDown className="w-4 h-4" />
                                  )}
                                  Reject
                                </Button>
                              </>
                            )}

                            {campaign.status === 'APPROVED' && (
                              <Button
                                onClick={() => handleStatusChange(campaign.id, 'ENDED')}
                                disabled={actionLoading === campaign.id}
                                variant="outline"
                                className="rounded-full gap-2"
                                data-testid={`end-campaign-${campaign.id}`}
                              >
                                {actionLoading === campaign.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Flag className="w-4 h-4" />
                                )}
                                End
                              </Button>
                            )}

                            {/* Delete Campaign - Available for all statuses */}
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="destructive"
                                  className="rounded-full gap-2"
                                  data-testid={`delete-campaign-${campaign.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                  Delete
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle className="flex items-center gap-2">
                                    <AlertTriangle className="w-5 h-5 text-destructive" />
                                    Delete Campaign Permanently
                                  </AlertDialogTitle>
                                  <AlertDialogDescription className="space-y-2">
                                    <p>Are you sure you want to permanently delete this campaign?</p>
                                    <p className="font-semibold text-foreground">"{campaign.title}"</p>
                                    <p className="text-destructive">This action cannot be undone. All associated donations, receipts, and comments will also be deleted.</p>
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleDeleteCampaign(campaign.id)}
                                    className="rounded-full bg-destructive hover:bg-destructive/90"
                                    disabled={actionLoading === campaign.id}
                                  >
                                    {actionLoading === campaign.id ? (
                                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                    ) : (
                                      <Trash2 className="w-4 h-4 mr-2" />
                                    )}
                                    Delete Permanently
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </PageLayout>
  );
}
